import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  BrowserRouter as Router, Routes, Route, Navigate
} from 'react-router-dom';
import {
  Users, Calendar, IndianRupee, FileText,
  CheckSquare, FileSignature, Settings, LogOut, Bell,
  CheckCircle, XCircle, Clock, Layers, Menu, Loader2, Video,
  User, ShieldCheck, ClipboardList, LayoutDashboard, CheckCheck, X
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import './index.css';
import logo from './assest/logo.png';

// Components
import Employees from './components/Employees';
import Attendance from './components/Attendance';
import LeaveRequests from './components/LeaveRequests';
import Payroll from './components/Payroll';
import Reports from './components/Reports';
import DailyReports from './components/DailyReports';
import TaskManager from './components/TaskManager';
import Agreements from './components/Agreements';
import SettingsPage from './components/SettingsPage';
import ProfileModal from './components/ProfileModal';
import NotificationsPage from './components/NotificationsPage';
import MeetingManager from './components/MeetingManager';
import Login from './components/Login';
import AdminLogin from './website/AdminLogin';
import HomePage from './website/HomePage';
import AboutUs from './website/AboutUs';
import SoftwareBranding from './website/SoftwareBranding';
import Solutions from './website/Solutions';
import api, { getImageUrl } from './api';
import { Toaster } from 'react-hot-toast';

// ── Web Audio notification sound ──
const playNotifSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.45);
  } catch (e) {}
};

const getNotifMeta = (type = '') => {
  const t = type.toLowerCase();
  if (t.includes('leave'))   return { color: 'bg-purple-500', textColor: 'text-purple-600', label: 'Leave' };
  if (t.includes('clockin') || t.includes('clock_in'))  return { color: 'bg-emerald-500', textColor: 'text-emerald-600', label: 'Clock In' };
  if (t.includes('clockout') || t.includes('clock_out')) return { color: 'bg-orange-500', textColor: 'text-orange-600', label: 'Clock Out' };
  if (t.includes('task'))    return { color: 'bg-blue-500',   textColor: 'text-blue-600',   label: 'Task' };
  return                       { color: 'bg-slate-400',        textColor: 'text-slate-600',   label: 'Notice' };
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

// ── TOP-LEVEL: SidebarItem — must be outside AppContent to avoid remount ──
const SidebarItem = ({ icon: Icon, label, active, onClick, collapsed, badge }) => (
  <button onClick={onClick} className={`w-full flex items-center py-[9px] px-6 transition-all group relative ${active ? 'bg-emerald-500/10 text-emerald-500 border-r-4 border-emerald-500' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
    <div className="relative shrink-0">
      <Icon size={19} className={active ? 'text-emerald-500' : 'text-slate-500 group-hover:text-slate-300'} />
      {badge > 0 && collapsed && (
        <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] bg-red-500 rounded-full text-[8px] text-white flex items-center justify-center font-black px-0.5">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </div>
    {!collapsed && <span className="ml-4 font-bold text-[11px] uppercase tracking-widest leading-none flex-1 text-left">{label}</span>}
    {!collapsed && badge > 0 && (
      <span className="min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center px-0.5 animate-pulse">
        {badge > 9 ? '9+' : badge}
      </span>
    )}
  </button>
);

// ── TOP-LEVEL: StatCard ──
const StatCard = ({ label, value, icon: Icon, color, onClick, blobColor, accent }) => {
  const colorMap = {
    blue: 'bg-blue-500', emerald: 'bg-emerald-500', red: 'bg-red-500',
    orange: 'bg-orange-600', purple: 'bg-purple-600', indigo: 'bg-indigo-600',
    teal: 'bg-teal-600', pink: 'bg-pink-600'
  };
  return (
    <div onClick={onClick} className="bg-white p-5 rounded-[8px] border border-gray-100 shadow-sm relative overflow-hidden flex justify-between items-start transition-all hover:shadow-lg cursor-pointer group h-[130px]">
      <div className={`absolute bottom-0 right-0 w-8 h-8 ${accent} opacity-10 rounded-tl-full`}></div>
      <div className={`absolute top-0 right-0 w-12 h-[3px] ${accent}`}></div>
      <div className={`absolute -top-4 -right-4 w-20 h-20 ${blobColor} opacity-30 rounded-full blur-xl group-hover:scale-110 transition-transform`}></div>
      <div className="space-y-0.5 relative z-10">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">{label}</p>
        <h4 className="text-[28px] font-black text-slate-800 tracking-tighter leading-none">{value}</h4>
        <p className="text-[10px] font-bold text-slate-300 mt-4 group-hover:text-emerald-500 transition-colors">Tap to view details</p>
      </div>
      <div className={`w-10 h-10 rounded-[6px] flex items-center justify-center text-white ${colorMap[color]} shadow-md relative z-10 transition-transform group-hover:rotate-3`}>
        <Icon size={18} strokeWidth={2.5} />
      </div>
    </div>
  );
};

// ── TOP-LEVEL: Dashboard ──
const Dashboard = ({ stats, setActiveTab }) => {
  if (!stats) return <div className="text-center py-20 font-bold text-slate-400">Loading dynamic data...</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Employees" value={stats.totalEmployees||0} icon={Users}       color="blue"    onClick={() => setActiveTab('Employees')}     blobColor="bg-blue-100"   accent="bg-blue-500" />
        <StatCard label="Present Today"   value={stats.presentToday||0}  icon={CheckCircle}  color="emerald" onClick={() => setActiveTab('Attendance')}     blobColor="bg-emerald-100" accent="bg-emerald-500" />
        <StatCard label="Absent Today"    value={stats.absentToday||0}   icon={XCircle}      color="red"     onClick={() => setActiveTab('Attendance')}     blobColor="bg-red-100"    accent="bg-red-500" />
        <StatCard label="Half Day"        value={stats.halfDayToday||0}  icon={Clock}        color="orange"  onClick={() => setActiveTab('Attendance')}     blobColor="bg-orange-100"  accent="bg-orange-600" />
        <StatCard label="Active Today"    value={stats.activeToday||0}   icon={CheckCircle}  color="blue"    onClick={() => setActiveTab('Attendance')}     blobColor="bg-blue-100"   accent="bg-blue-500" />
        <StatCard label="Pending Tasks"   value={stats.pendingTasks||0}  icon={ShieldCheck}  color="indigo"  onClick={() => setActiveTab('Task Manager')}  blobColor="bg-indigo-100"  accent="bg-indigo-600" />
        <StatCard label="Departments"     value={stats.departments||0}   icon={Layers}       color="teal"    onClick={() => setActiveTab('Dashboard')}      blobColor="bg-teal-100"    accent="bg-teal-600" />
        <StatCard label="Total Tasks"     value={stats.totalTasks||0}    icon={FileText}     color="pink"    onClick={() => setActiveTab('Task Manager')}  blobColor="bg-pink-100"    accent="bg-pink-600" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
        <div className="lg:col-span-2 bg-white p-6 rounded-[8px] shadow-sm border border-gray-100">
          <h3 className="text-[13px] font-black text-slate-400 mb-6 uppercase tracking-widest">Attendance Trend</h3>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.attendanceTrend||[]}>
                <defs>
                  <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize:10,fontWeight:'bold'}} />
                <YAxis axisLine={false} tickLine={false} tick={{fontSize:10,fontWeight:'bold'}} />
                <Tooltip contentStyle={{borderRadius:'8px',border:'none',boxShadow:'0 5px 20px rgba(0,0,0,0.05)'}} />
                <Area type="monotone" dataKey="present" stroke="#10b981" strokeWidth={2} fill="url(#colorVisits)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white p-6 rounded-[8px] shadow-sm border border-gray-100 no-scrollbar overflow-y-auto">
          <h3 className="text-[13px] font-black text-slate-400 mb-6 uppercase tracking-widest">Recent Updates</h3>
          <div className="space-y-4">
            {(stats.recentActivity && stats.recentActivity.length > 0) ? stats.recentActivity.slice(0, 6).map((act,i) => {
              const getInitials = (name) => {
                if (!name) return 'U';
                const parts = name.trim().split(/\s+/);
                if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
                return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
              };

              return (
                <div 
                  key={i} 
                  onClick={() => setActiveTab('Attendance')}
                  className="flex items-center gap-3 p-1.5 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors border border-transparent hover:border-slate-100 group"
                >
                  <div className="w-9 h-9 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 font-black overflow-hidden text-[11px] shadow-sm">
                    {act.profile_photo_url ? (
                      <img src={act.profile_photo_url} className="w-full h-full object-cover" alt="" />
                    ) : (
                      getInitials(act.user)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11.5px] font-black text-slate-800 truncate group-hover:text-emerald-600 transition-colors uppercase tracking-tight">{act.user}</p>
                      <div className="text-[8px] font-black text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded shrink-0 border border-slate-100 italic">{act.time}</div>
                    </div>
                    <p className="text-[9.5px] font-bold text-slate-500 truncate mt-0.5">
                      <span className="text-emerald-500">{act.action}</span> • <span className="opacity-70 italic">{act.role}</span>
                    </p>
                  </div>
                </div>
              );
            }) : <p className="text-center text-[10px] text-slate-400 py-10">No updates.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── TOP-LEVEL: AdminDashboard — MUST be outside AppContent to prevent remount ──
const AdminDashboard = ({
  user, stats, isSidebarOpen, setIsSidebarOpen,
  activeTab, setActiveTab,
  notifications, unreadCount,
  showBellDropdown, setShowBellDropdown,
  markAllRead, markingAll,
  isProfileModalOpen, setIsProfileModalOpen,
  isDropdownOpen, setIsDropdownOpen,
  handleLogout, handleProfileUpdate
}) => {
  const bellRef = useRef(null);

  // Close bell dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setShowBellDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [setShowBellDropdown]);

  const handleNotifClick = (notif) => {
    const data = notif.data || {};
    const t = (notif.type || data.type || '').toLowerCase();
    const txt = (data.title || data.message || '').toLowerCase();
    const combined = t + ' ' + txt;

    if (combined.includes('leave')) setActiveTab('Leave Requests');
    else if (combined.includes('task')) setActiveTab('Task Manager');
    else if (combined.includes('report')) setActiveTab('Daily Reports');
    else if (combined.includes('clock') || combined.includes('punch') || combined.includes('attendance')) setActiveTab('Attendance');
    else if (combined.includes('meeting')) setActiveTab('Meetings');
    else setActiveTab('Notifications');

    setShowBellDropdown(false);
  };

  return (
    <div className="flex h-screen bg-[#f8fafc] text-slate-700 overflow-hidden font-['Roboto',sans-serif]">
      {/* Sidebar */}
      <aside className={`bg-[#0f172a] text-gray-400 transition-all duration-300 flex flex-col z-50 relative ${isSidebarOpen ? 'w-[235px]' : 'w-[80px]'}`}>
        <div className="h-16 flex items-center px-4 gap-3 shrink-0">
          <div className="w-9 h-9 bg-white rounded-[6px] shadow-sm flex items-center justify-center p-1.5 shrink-0 border border-slate-700/50">
            <img src={logo} alt="Logo" className="w-full h-full object-contain" />
          </div>
          {isSidebarOpen && (
            <div className="leading-tight">
              <p className="font-black text-white text-[11px] uppercase tracking-wider">Secure Insights</p>
              <p className="font-bold text-emerald-500 text-[8px] uppercase tracking-[0.2em]">Forever</p>
            </div>
          )}
        </div>
        <div className="border-b border-slate-800 w-full mb-1"></div>
        <nav className="flex-1 py-2 space-y-0.5 overflow-y-auto overflow-x-hidden no-scrollbar">
          <SidebarItem icon={LayoutDashboard} label="Dashboard"     active={activeTab==='Dashboard'}     onClick={() => setActiveTab('Dashboard')}     collapsed={!isSidebarOpen} />
          <SidebarItem icon={Users}           label="Employees"     active={activeTab==='Employees'}     onClick={() => setActiveTab('Employees')}     collapsed={!isSidebarOpen} />
          <SidebarItem icon={CheckCircle}     label="Attendance"    active={activeTab==='Attendance'}    onClick={() => setActiveTab('Attendance')}    collapsed={!isSidebarOpen} />
          <SidebarItem icon={Calendar}        label="Leave Requests" active={activeTab==='Leave Requests'} onClick={() => setActiveTab('Leave Requests')} collapsed={!isSidebarOpen} />
          <SidebarItem icon={IndianRupee}     label="Payroll"       active={activeTab==='Payroll'}       onClick={() => setActiveTab('Payroll')}       collapsed={!isSidebarOpen} />
          <SidebarItem icon={FileText}        label="Reports"       active={activeTab==='Reports'}       onClick={() => setActiveTab('Reports')}       collapsed={!isSidebarOpen} />
          <SidebarItem icon={FileSignature}   label="Daily Reports" active={activeTab==='Daily Reports'} onClick={() => setActiveTab('Daily Reports')} collapsed={!isSidebarOpen} />
          <SidebarItem icon={CheckSquare}     label="Task Manager"  active={activeTab==='Task Manager'}  onClick={() => setActiveTab('Task Manager')}  collapsed={!isSidebarOpen} />
          <SidebarItem icon={Video}           label="Meetings"      active={activeTab==='Meetings'}      onClick={() => setActiveTab('Meetings')}      collapsed={!isSidebarOpen} />
          <SidebarItem icon={ClipboardList}   label="Agreements"    active={activeTab==='Agreements'}    onClick={() => setActiveTab('Agreements')}    collapsed={!isSidebarOpen} />
          <SidebarItem icon={Bell}            label="Notifications" active={activeTab==='Notifications'} onClick={() => setActiveTab('Notifications')} collapsed={!isSidebarOpen} badge={unreadCount} />
          <SidebarItem icon={Settings}        label="Settings"      active={activeTab==='Settings'}      onClick={() => setActiveTab('Settings')}      collapsed={!isSidebarOpen} />
        </nav>
        <div className="p-4 border-t border-slate-800">
          <button onClick={handleLogout} className="flex items-center justify-center space-x-2 w-full p-2.5 rounded-[6px] bg-red-500 hover:bg-red-600 text-white font-black text-[11px] uppercase tracking-widest shadow-lg shadow-red-500/20 active:scale-[0.98] transition-all">
            <LogOut size={16} />
            {isSidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] flex items-center justify-between px-6 z-40 shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 rounded-[6px] transition-all duration-200">
              <Menu size={24} />
            </button>
            <h2 className="text-[16px] font-black text-[#1e293b]">Welcome Back, {user?.name || 'Admin'} 👋</h2>
          </div>

          <div className="flex items-center space-x-3">
            {/* Bell */}
            <div className="relative" ref={bellRef}>
              <button
                onClick={() => setShowBellDropdown(prev => !prev)}
                className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-[6px] transition-all relative"
              >
                <Bell size={20} className={unreadCount > 0 ? 'text-indigo-500' : ''} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] bg-red-500 rounded-full border-2 border-white text-[8px] text-white flex items-center justify-center font-black px-0.5 animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Bell Dropdown */}
              {showBellDropdown && (
                <div className="absolute right-0 top-12 w-[340px] bg-white rounded-xl shadow-2xl border border-slate-100 z-[300] overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50">
                    <div className="flex items-center gap-2">
                      <Bell size={14} className="text-indigo-500" />
                      <span className="text-[12px] font-black text-slate-800">Notifications</span>
                      {unreadCount > 0 && (
                        <span className="text-[9px] font-black bg-red-500 text-white px-1.5 py-0.5 rounded-full">{unreadCount} NEW</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} disabled={markingAll} className="text-[10px] font-black text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                          {markingAll ? <Loader2 size={10} className="animate-spin" /> : <CheckCheck size={10} />} Mark Read
                        </button>
                      )}
                      <button onClick={() => { setActiveTab('Notifications'); setShowBellDropdown(false); }} className="text-[10px] font-black text-indigo-500 hover:text-indigo-700">
                        View All →
                      </button>
                    </div>
                  </div>

                  {/* List */}
                  <div className="max-h-[320px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 gap-2">
                        <Bell size={24} className="text-slate-200" />
                        <p className="text-[11px] font-bold text-slate-400">All caught up!</p>
                      </div>
                    ) : notifications.slice(0, 12).map((notif, idx) => {
                      const data = notif.data || {};
                      const isUnread = !notif.read_at;
                      const meta = getNotifMeta(notif.type || data.type || '');
                      return (
                        <div
                          key={notif.id || idx}
                          onClick={() => handleNotifClick(notif)}
                          className={`flex items-start gap-3 px-4 py-2.5 border-b border-gray-50 cursor-pointer transition-colors hover:bg-indigo-50/60 active:bg-indigo-100/60 ${isUnread ? 'bg-indigo-50/30' : 'hover:bg-gray-50/40'}`}
                        >
                          <div className={`w-1.5 h-1.5 rounded-full ${meta.color} mt-2 shrink-0`}></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className={`text-[9px] font-black uppercase ${meta.textColor}`}>{meta.label}</span>
                              {isUnread && <span className="text-[8px] font-black text-red-500 flex items-center gap-0.5 animate-pulse"><span className="w-1 h-1 bg-red-500 rounded-full"></span>NEW</span>}
                            </div>
                            <p className="text-[11px] font-bold text-slate-800 leading-snug line-clamp-2">{data.title || data.message || 'Notification'}</p>
                            {(data.employee_name || data.user_name) && (
                              <p className="text-[10px] text-slate-400 mt-0.5">👤 {data.employee_name || data.user_name}</p>
                            )}
                          </div>
                          <span className="text-[9px] text-slate-400 shrink-0">{timeAgo(notif.created_at)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
                    <button onClick={() => { setActiveTab('Notifications'); setShowBellDropdown(false); }} className="w-full text-center text-[11px] font-black text-indigo-600 hover:text-indigo-800">
                      Open Full Notifications Center →
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User dropdown */}
            <div className="flex items-center gap-3 relative">
              <div className="h-8 w-[1px] bg-slate-100 hidden sm:block"></div>
              <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-3 text-left pl-2">
                <div className="w-9 h-9 rounded-full bg-emerald-500 overflow-hidden border-2 border-emerald-100 p-0.5 shadow-sm">
                  <img src={getImageUrl(user?.profile_photo_path) || `https://ui-avatars.com/api/?name=${user?.name}`} className="w-full h-full rounded-full object-cover" alt="avatar" />
                </div>
                <div className="hidden sm:block leading-tight">
                  <p className="text-[12.5px] font-black text-slate-800">{user?.name || 'Admin'}</p>
                  <p className="text-[9.5px] font-bold text-slate-400">Administrator</p>
                </div>
              </button>
              {isDropdownOpen && (
                <div className="absolute right-0 top-12 w-48 bg-white rounded-[6px] shadow-2xl border border-slate-100 py-2 z-[200]">
                  <button onClick={() => { setIsProfileModalOpen(true); setIsDropdownOpen(false); }} className="w-full text-left px-5 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 flex items-center gap-2">
                    <User size={14} /> Profile Settings
                  </button>
                  <button onClick={handleLogout} className="w-full text-left px-5 py-2.5 text-xs font-bold text-red-500 hover:bg-red-50 flex items-center gap-2 border-t border-slate-50">
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-[10px] bg-[#f8fafc] no-scrollbar">
          {activeTab === 'Dashboard'      && <Dashboard stats={stats} setActiveTab={setActiveTab} />}
          {activeTab === 'Employees'      && <Employees />}
          {activeTab === 'Attendance'     && <Attendance />}
          {activeTab === 'Leave Requests' && <LeaveRequests />}
          {activeTab === 'Payroll'        && <Payroll />}
          {activeTab === 'Reports'        && <Reports />}
          {activeTab === 'Daily Reports'  && <DailyReports />}
          {activeTab === 'Task Manager'   && <TaskManager />}
          {activeTab === 'Meetings'       && <MeetingManager />}
          {activeTab === 'Agreements'     && <Agreements />}
          {activeTab === 'Notifications'  && <NotificationsPage setActiveTab={setActiveTab} />}
          {activeTab === 'Settings'       && <SettingsPage user={user} onUpdate={handleProfileUpdate} />}
        </main>
      </div>

      <ProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} user={user} onUpdate={handleProfileUpdate} />
    </div>
  );
};

// ── AppContent ──
function AppContent() {
  const [isSidebarOpen, setIsSidebarOpen]     = useState(true);
  const [activeTab, setActiveTab]             = useState(() => {
    return localStorage.getItem('activeTab') || 'Dashboard';
  });

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);
  const [user, setUser]                       = useState(null);
  const [stats, setStats]                     = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen]   = useState(false);
  const [notifications, setNotifications]     = useState([]);
  const [showBellDropdown, setShowBellDropdown] = useState(false);
  const [markingAll, setMarkingAll]           = useState(false);
  const prevUnreadCount                       = useRef(0);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user && activeTab === 'Dashboard') fetchStats();
  }, [user, activeTab]);

  // Notification polling
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/admin/notifications');
      const data = Array.isArray(res.data) ? res.data : (res.data || []);
      const unread = data.filter(n => !n.read_at).length;
      if (unread > prevUnreadCount.current) playNotifSound();
      prevUnreadCount.current = unread;
      setNotifications(data);
    } catch (err) { /* silent fail */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  const markAllRead = useCallback(async () => {
    setMarkingAll(true);
    try {
      await api.post('/admin/notifications/read', {});
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      prevUnreadCount.current = 0;
    } catch (err) { /* silent */ }
    finally { setMarkingAll(false); }
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
    } catch (err) { console.error('Dashboard Stats Error:', err); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/';
  };

  const handleProfileUpdate = (updatedUser) => {
    const newUser = { ...user, ...updatedUser };
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-emerald-500" size={40} />
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.read_at).length;

  return (
    <>
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            fontSize: '13px',
            fontWeight: 'bold',
            color: '#1e293b',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid #f1f5f9',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          },
          success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
          duration: 4000
        }} 
      />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/features" element={<SoftwareBranding />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/login" element={<Login onLoginSuccess={setUser} />} />
        <Route path="/admin" element={user ? <Navigate to="/admin/dashboard" /> : <AdminLogin setUser={setUser} />} />
        <Route path="/admin/dashboard" element={
          user ? (
            <AdminDashboard
              user={user}
              stats={stats}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              notifications={notifications}
              unreadCount={unreadCount}
              showBellDropdown={showBellDropdown}
              setShowBellDropdown={setShowBellDropdown}
              markAllRead={markAllRead}
              markingAll={markingAll}
              isProfileModalOpen={isProfileModalOpen}
              setIsProfileModalOpen={setIsProfileModalOpen}
              isDropdownOpen={isDropdownOpen}
              setIsDropdownOpen={setIsDropdownOpen}
              handleLogout={handleLogout}
              handleProfileUpdate={handleProfileUpdate}
            />
          ) : <Navigate to="/admin" />
        } />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
